import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { Room } from 'src/app/models/room.model';
import { ClientcommunicationService } from 'src/app/services/clientcommunication/clientcommunication.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonFunctions } from 'src/app/Utilities/CommonFunctions';

@Component({
  selector: 'app-chat-sidebar',
  templateUrl: './chat-sidebar.component.html',
  styleUrls: ['./chat-sidebar.component.css']
})
export class ChatSidebarComponent implements OnInit, OnDestroy {
  room: Room = new Room();
  rooms: Room[] = [];
  subs: Subscription[] = [];
  lastMessage: string = '';
  searchText = '';
  @Input() userId: string = '';
  subsForGet?: Subscription;
  paramValue: string = '';
  subscribeNotify: any;

  private navigated = false;
  private roomUpdatedSub?: Subscription;
  private dailyTimeoutId: any;
  private dailyIntervalId: any;

  constructor(
    private clientcommunicationService: ClientcommunicationService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private cdRef: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadRooms();

    this.clientcommunicationService.connectChatSocket();
    this.clientcommunicationService.joinSystemAdminChannel();
    this.roomUpdatedSub = this.clientcommunicationService.onRoomUpdated().subscribe(() => {
      this.loadRooms();
    });

    this.initializeDailySubscription();
  }

  ngAfterViewInit(): void {
    this.cdRef.detectChanges();
    this.subsForGet = this.clientcommunicationService.pathParam.subscribe(value => {
      this.paramValue = value;
    });
  }

  initializeDailySubscription(): void {
    const now = new Date();
    const targetTime = new Date();
    targetTime.setHours(1, 0, 0, 0);
    if (now.getTime() > targetTime.getTime()) {
      targetTime.setDate(targetTime.getDate() + 1);
    }
    const timeUntilNextRun = targetTime.getTime() - now.getTime();

    this.dailyTimeoutId = setTimeout(() => {
      this.subscribeNotification();

      this.dailyIntervalId = setInterval(() => {
        const current = new Date();
        const nextRun = new Date();
        nextRun.setHours(1, 0, 0, 0);
        nextRun.setDate(current.getDate() + 1);
        const delay = nextRun.getTime() - current.getTime();
        setTimeout(() => {
          this.subscribeNotification();
        }, delay);
      }, 24 * 60 * 60 * 1000);
    }, timeUntilNextRun);
  }

  /** Fetch every support room (one per business) from the server and re-render the sidebar. */
  loadRooms(): void {
    this.clientcommunicationService.getSupportRoomsRest().subscribe((response: any) => {
      const data = response?.data || [];
      this.rooms = data
        .map((conv: any) => this.mapConversationToRoom(conv))
        .sort((a: Room, b: Room) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      if (!this.navigated && this.rooms.length > 0) {
        this.router.navigate(['client-communication/room/' + this.rooms[0].id]);
        this.navigated = true;
      }
      this.cdRef.markForCheck();
    });
  }

  /** Maps the REST ChatConversation shape onto the existing snake_case Room model the template expects. */
  private mapConversationToRoom(conv: any): Room {
    const room: any = {
      id: conv.id,
      business_id: conv.businessId,
      business_name: conv.businessName,
      business_logo: conv.businessLogo,
      recepient_id: conv.recipientId,
      recepient_name: conv.recipientName,
      last_message: conv.lastMessage,
      preference_type: conv.preferenceType,
      // Admin-side read state, independent of the business's own readStatus.
      read_status: conv.adminReadStatus,
      timestamp: conv.timestamp,
      timeTag: CommonFunctions.getTimeAndDateTag(conv.timestamp, 'sidebar')
    };
    return room;
  }

  subscribeNotification() {
    this.clientcommunicationService.subscribeNotificationList().subscribe(
      (response: any) => {
        var json = JSON.parse(JSON.stringify(response));
        if (json.response.status == 'SUCCESS') {
          this.subscribeNotify = json.data;
        }
        this.processSubscribeNotify();
      },
      (error) => {
        console.log(error);
      }
    );
  }

  processSubscribeNotify() {
    this.subscribeNotify.forEach((subscription: any) => {
      const businessId = subscription.businessInfoDetails.businessId;
      const room = this.rooms.find(room => room.business_id === businessId);
      const message = this.generateSubscriptionMessage(subscription);
      if (room?.id) {
        this.clientcommunicationService.sendSupportMessageRest(room.id, message, []).subscribe(() => {
          this.loadRooms();
        }, (error) => {
          console.log(error);
        });
      }
      this.sendEmailNotification(message, subscription.businessInfoDetails.id);
    });
  }

  generateSubscriptionMessage(subscription: any): string {
    const businessName = subscription.businessInfoDetails.name;
    const expiryDate = subscription.userSubscription.expiry.split("T")[0];
    const subscriptionType = subscription.businessInfoDetails.subscriptionType;

    if (subscriptionType === 'FREE') {
      return `Dear ${businessName},

Your FOREVER FREE subscription will expire on ${expiryDate}. Hope you are seeing the value of using the Chatstasy portal for your business. At this time, we would encourage you to upgrade to BASIC or STANDARD plan and take advantage of the maintenance fee refund. If you choose to continue using our FOREVER FREE plan, please renew by paying the annual maintenance fee of $20 to prevent disruption from accessing your business portal.

Please do not hesitate to contact us if you need assistance renewing your subscription or if you have any questions.

Thank you very much for your business.
Chatstasy Support Team.`;
    } else {
      return `Dear ${businessName},

Your (monthly or annual subscription) will expire on ${expiryDate}. Please renew your subscription to prevent disruption from accessing your business portal.

Please do not hesitate to contact us if you need assistance renewing your subscription.

Thank you very much for your business.
Chatstasy Support Team.`;
    }
  }

  sendEmailNotification(emailBody: string, businessId: string) {
    this.clientcommunicationService.sendSubscriptionEmail(emailBody, businessId).subscribe(
      (response: any) => {
        var json = JSON.parse(JSON.stringify(response));
        console.log(json.response.status);
      },
      (error) => {
        console.log(error);
      });
  }

  removeHTMLTag(html: any) {
    return html ? html.replace(/(<([^>]+)>)/ig, '').replace(/&nbsp;/g, '') : '';
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    this.subsForGet?.unsubscribe();
    this.roomUpdatedSub?.unsubscribe();
    this.clientcommunicationService.leaveSystemAdminChannel();
    if (this.dailyTimeoutId) clearTimeout(this.dailyTimeoutId);
    if (this.dailyIntervalId) clearInterval(this.dailyIntervalId);
  }
}
